import React, { useCallback, useMemo, useRef } from 'react';
import { StyleSheet, View } from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import { useAppDispatch } from 'store/hooks';
import { Button, Spacer } from 'design-system';
import Text from 'design-system/Text';
import { useAppState } from 'hooks';
import useColors from 'hooks/useColors';
import ProgressCard from 'features/invest/components/InvestOnboardingFunnel/ProgressCard';
import { getConnectedAccount } from 'actions/invest';
import { OnboardingFlow } from 'utils/types/navigation';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import { showUnavailableOnWeb } from 'utils/webGate';
import { isDesktop } from '../../../constants';
import { getPotsAccount } from '../actions';
import useAccount from '../hooks/useAccount';
import { showContinueInMobileAlert } from '../utils';
const strings = {
    completedTitle: 'Verified',
    incompleteTitle: 'Under review',
    actionRequiredTitle: 'Action required',
    desc: 'We’ll let you know once your account is approved. This process usually takes minutes',
    actionRequiredDesc: 'We need to verify your details for regulatory purposes. Tap “verify” to provide both proofs of your identity and address',
};
const UnderReviewProgressCard = ({ status, lastItem }) => {
    const colors = useColors();
    const dispatch = useAppDispatch();
    const navigation = useMainStackNavigation();
    const account = useAccount();
    const requiredUserAction = account?.kycVerification?.requiredUserAction;
    // if we have rejectionString, it means that the document is rejected and we should show document reupload context
    const rejectionReasonString = account?.kycVerification?.rejections?.[0]?.rejectionReasonString;
    const requestingRef = useRef(false);
    const completed = status === 'ACTIVE';
    const isActiveStep = status !== 'ACTIVE';
    const isActionRequired = status === 'KYC_PENDING' && !!requiredUserAction;
    const loadData = useCallback(async () => {
        if (!requestingRef.current) {
            requestingRef.current = true;
            await dispatch(getPotsAccount());
            await dispatch(getConnectedAccount());
            requestingRef.current = false;
        }
    }, []);
    useFocusEffect(useCallback(() => {
        loadData();
    }, [loadData]));
    const appStateChangeCallbacks = useMemo(() => ({
        didBecomeActive: loadData,
    }), [loadData]);
    useAppState(appStateChangeCallbacks);
    const handleVerify = useCallback(() => {
        if (showUnavailableOnWeb('emma://save?tab=pots', 'Verify your identity')) {
            return;
        }
        if (isDesktop) {
            showContinueInMobileAlert();
            return;
        }
        switch (requiredUserAction) {
            case 'UPLOAD_IDENTITY_DOCUMENT':
                navigation.navigateDeprecated('WealthDocumentUploadIntroScreen', {
                    documentType: 'POI',
                    flow: OnboardingFlow.Pots,
                });
                break;
            case 'UPLOAD_PROOF_OF_ADDRESS':
                navigation.navigateDeprecated('WealthDocumentUploadIntroScreen', {
                    documentType: 'POA',
                    flow: OnboardingFlow.Pots,
                });
                break;
            default:
            // Gets here when the status is 'KYC_PENDING', but `requiredUserAction` is not set.
        }
    }, [requiredUserAction]);
    return (<ProgressCard lightBg lastItem={lastItem} completed={completed} isActiveStep={isActiveStep} isActionRequired={isActionRequired} title={
        // eslint-disable-next-line no-nested-ternary
        isActionRequired
            ? strings.actionRequiredTitle + (rejectionReasonString ? ': Failed verification' : '')
            : completed
                ? strings.completedTitle
                : strings.incompleteTitle}>
      <Text TextThin-14 Secondary>
        {isActionRequired ? rejectionReasonString || strings.actionRequiredDesc : strings.desc}
      </Text>
      {!!isActionRequired && (<>
          <Spacer h={12}/>
          <View style={styles.row}>
            <Button hug extra custom square onPress={handleVerify} textColor={colors.text.white} containerStyle={styles.btnContainer} gradientColors={[colors.elements.statusNegative, colors.elements.statusNegative]} title={rejectionReasonString ? 'Reverify' : 'Verify'}/>
          </View>
        </>)}
    </ProgressCard>);
};
const styles = StyleSheet.create({
    row: {
        flexDirection: 'row',
    },
    btnContainer: {
        paddingHorizontal: 0,
    },
});
export default UnderReviewProgressCard;
